import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import SaveButton from '../components/psc/lib/SaveButton'
import ErrorMessage from '../components/psc/lib/ErrorMessage'
import CancelButton from '../components/psc/lib/CancelButton'
import DeleteButton from '../components/psc/lib/DeleteButton'
import LinkedItems from '../components/psc/lib/LinkedItems'
import SingleSelect from '../components/psc/lib/SingleSelect'
import TextArea from '../components/psc/lib/TextArea'
import PickDate from '../components/psc/lib/PickDate'
import Loading from '../components/psc/lib/Loading'
import NoAccess from '../components/psc/lib/NoAccess'

import useAuthentication from '../hooks/useAuthentication';
import useUserManagement from '../hooks/useUserManagement';
import useProjectContents from '../hooks/useProjectContents';

const ActionItemSource = () => {
    const navigate = useNavigate()
    const pscState = useSelector(state => state.pscState.value)
    const project = pscState.project
    const params = useParams()
    const isNew = params.objID === 'new';
    const projectID = Number(params.projectID)
    const objID = Number(params.objID)
    
    const { currentUser } = useAuthentication();

    const { userHasProjectAccessLevel } = useUserManagement()

    const {
        updateActionItemSource,
        deleteActionItemSource,
    } = useProjectContents()
    
    const [id, setID] = useState(null)
    const [updatedAt, setUpdatedAt] = useState(null)
    const [name, setName] = useState('')
    const [sourceDate, setSourceDate] = useState(new Date())
    const [location, setLocation] = useState('')
    const [participants, setParticipants] = useState('')
    const [defaultItemType, setDefaultItemType] = useState('External')
    const [linkedItems, setLinkedItems] = useState([])

    const [isUsed, setIsUsed] = useState(false)
    const [error, setError] = useState([])
    const [submitActive, setSubmitActive] = useState(false)
    const [submitSpinner, setSubmitSpinner] = useState(false)
    const [hasLoaded, setHasLoaded] = useState(isNew ? true : false)
    const [hasAccess, setHasAccess] = useState(true)

    useEffect(() => {
        if (isNew) {
            setSourceDate(new Date())
            return
        }

        fetchObject()
    }, [objID, isNew])

    const fetchObject = () => {
        fetch('/v0/action_item_sources/' + objID, { credentials: 'same-origin' })
            .then(function(response) {
                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }
                return response.json();
            })
            .then(function(json) {
                if (json.error || json.result.id === 0) {
                    throw new Error(json.error);
                }
                localStorage.defaultActionItemSourceID = parseInt(objID, 10)
                setID(json.result.id)
                setName(json.result.name)
                setLocation(json.result.location)
                setParticipants(json.result.participants)
                setDefaultItemType(json.result.default_item_type)
                setSourceDate(new Date(json.result.source_date))
                setLinkedItems(json.result.linked_items)
                setIsUsed(json.result.is_used)
                setUpdatedAt(json.result.updated_at)
                setHasLoaded(true)
            })
            .catch(function(error) {
                console.log('Error fetching action item source:', error);
                setHasAccess(false);
            })
    }

    const toBackend = () => {
        return {
            updated_at: updatedAt,
            name: name,
            source_date: sourceDate,
            location: location,
            participants: participants,
            default_item_type: defaultItemType,
            project_id: projectID,
            linked_items: linkedItems
        }
    }

    const handleSubmitError = (props) => {
        setSubmitActive(false)
        setSubmitSpinner(false)
        setError([ props.message ])
    }

    const handleSubmit = (event) => {
        if( event ) {
            event.preventDefault()
        }

        if( submitActive ) {
            return null
        }

        let error = []

        if ( ! name ) {
            error.push('Name is required')
        }

        if ( ! sourceDate ) {
            error.push('Date is required')
        }

        if ( error.length > 0 ) {
            setError(error)
            return null
        }

        setSubmitActive(true)
        let submit = {
            objType: 'action_item_sources',
            objID: objID,
            data: toBackend(),
            successCallback: function(json) {
                updateActionItemSource(json.result)
            }
        }
        return submit
    }

    const allOpenItemsClick = () => {
        const key = 'actionItemsSourceFilter_' + projectID
        const value = [{
            label: name,
            value: name
        }]
        localStorage[key] = JSON.stringify(value)
        navigate('/portfolio/'+projectID+'/action_items')
    }

    if (! projectID > 0 ) {
        return <h1>Loading...</h1>
    }

    let deleteButton = []
    if ( !isNew && userHasProjectAccessLevel(currentUser, projectID, 'MEMBER') && ! isUsed ) {
        var url = '/v0/action_item_sources/'+objID;
        deleteButton.push(
            <DeleteButton
                url={url}
                onError={handleSubmitError}
                callback={() => deleteActionItemSource(objID)}
            />
        )
    }

    let globalDisabled = false
    if( ! userHasProjectAccessLevel(currentUser, projectID, 'MEMBER')) {
        globalDisabled = true
    }

    let item_types = [
        { label: 'External', value: 'External', className: 'indent' },
        { label: 'Internal', value: 'Internal', className: 'indent' }
    ]

    let meetingActionItems = []
    if ( id ) {
        meetingActionItems.push(
            <div className="form-group" key="meeting-action-items">
                <div className="col-sm-10 col-sm-offset-2">
                    <a href="#" onClick={allOpenItemsClick}>Meeting Action Items</a>
                </div>
            </div>
        )
    }

    let requiredIndicator = '';
    if (userHasProjectAccessLevel(currentUser, projectID, 'MEMBER')) {
        requiredIndicator = (<label className="form-required-star">&nbsp;*</label>)
    }

    if (!hasAccess) {
        return <NoAccess />
    }

    if (!hasLoaded) {
        return <Loading />
    }

    return (
        <div className="wrapper wrapper-content">
            <div className="row">
                <div className="col-lg-12">
                    <div className="ibox float-e-margins">
                        <div className="ibox-title">
                            <h2>Meeting: {name || '<New Meeting>'}</h2>
                        </div>
                        <div className="ibox-content">
                            <form className="form-horizontal" onSubmit={(e) => handleSubmit(e)}>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Name
                                        {requiredIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text" name="name" value={name}
                                            onChange={(e) => setName(e.target.value)} required disabled={globalDisabled} />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Date
                                        {requiredIndicator}
                                    </label>
                                    <div className="col-sm-8">
                                        <PickDate
                                            value={sourceDate}
                                            onChange={(date) => setSourceDate(date)}
                                            disabled={globalDisabled}
                                        />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Location</label>
                                    <div className="col-sm-8">
                                        <input className="form-control" type="text"
                                            name="location" value={location}
                                            onChange={(e) => setLocation(e.target.value)} disabled={globalDisabled} />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">Participants</label>
                                    <div className="col-sm-8">
                                        <TextArea className="form-control"
                                            name="participants" value={participants}
                                            onChange={(e) => setParticipants(e.target.value)} disabled={globalDisabled} />
                                    </div>
                                </div>

                                <div className="form-group">
                                    <label className="col-sm-2 control-label">
                                        Meeting Type
                                        <br />
                                        <small style={{fontWeight: 'normal'}}>(Default Action Type)</small>
                                    </label>
                                    <div className="col-sm-8">
                                        { globalDisabled &&
                                            <input className="form-control" type="text" disabled={true} value={defaultItemType} />
                                            ||
                                            <SingleSelect
                                                name="default_item_type"
                                                value={defaultItemType}
                                                onChange={(item) => setDefaultItemType(item.value)}
                                                options={item_types}
                                                clearable={false}
                                            />
                                        }
                                    </div>
                                </div>

                                <div className="hr-line-dashed"></div>

                                <LinkedItems
                                    header="Meeting Minutes and Supporting Documents"
                                    project_id={projectID}
                                    linkedItems={linkedItems}
                                    disabled={ ! userHasProjectAccessLevel(currentUser, projectID, 'MEMBER') }
                                    onChange={(value) => setLinkedItems(value)}
                                />

                                <div className="hr-line-dashed"></div>

                                {meetingActionItems}

                                <div className="form-group">
                                    <ErrorMessage
                                        error={error}
                                    />

                                    <div className="col-sm-10 col-sm-offset-2">
                                        <CancelButton />

                                        <SaveButton
                                            buttonVariant='primary'
                                            visible={userHasProjectAccessLevel(currentUser,projectID, "MEMBER")}
                                            spinnerActive={submitActive}
                                            label='Save Changes'
                                            onClick={handleSubmit}
                                            onError={handleSubmitError}
                                        />


                                        {deleteButton}

                                    </div>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
};

export default ActionItemSource
